/**
 * Game State Manager
 * 
 * Manages the complete game state including:
 * - Current resources (with real-time accumulation)
 * - Building construction queues
 * - Active timers
 * - City state persistence
 */

import { GameEngine } from '../core/GameEngine.js';
import { TimerManager } from './TimerManager.js';
import { SaveManager } from './SaveManager.js';
import { ConstructionManager } from './ConstructionManager.js';
import { CurrencyManager } from './CurrencyManager.js';
import { WorldMapManager } from './WorldMapManager.js';
import { apiClient } from '../utils/APIClient.js';

export class GameState {
    constructor(gameEngine = null) {
        // Reference to GameEngine for calculations
        this.gameEngine = gameEngine || new GameEngine();
        
        // Timer and save managers
        this.timerManager = new TimerManager();
        this.saveManager = new SaveManager();
        this.constructionManager = new ConstructionManager(this, this.gameEngine);
        this.currencyManager = new CurrencyManager(this, apiClient);
        this.worldMapManager = new WorldMapManager(this);
        
        // Current resources (actual amounts player has)
        this.resources = {
            wood: 0,
            stone: 0,
            iron: 0,
            food: 0,
            gold: 0
        };
        
        // Storage capacities (calculated from buildings)
        this.storage = {
            wood: 0,
            stone: 0,
            iron: 0,
            food: 0
        };
        
        // Hidden resources (in caches)
        this.hiddenResources = {
            wood: 0,
            stone: 0,
            iron: 0,
            food: 0
        };
        
        // Last update timestamp (for calculating elapsed time)
        this.lastUpdateTime = Date.now();
        
        // Game mode state
        this.isPaused = false;
        this.gameSpeed = 1.0; // 1x, 2x, 4x speed multiplier
        
        // Construction queue (managed by ConstructionManager)
        // Access via this.constructionManager.getQueue()
        
        // Event listeners
        this.eventListeners = {
            resourceUpdated: [],
            buildingCompleted: [],
            constructionStarted: [],
            constructionCancelled: []
        };
    }
    
    /**
     * Initialize game state (call after setting up GameEngine)
     */
    initialize() {
        // Calculate initial storage
        this.updateStorage();
        
        // Set initial update time
        this.lastUpdateTime = Date.now();
    }
    
    /**
     * Update game state (called by GameLoop)
     * @param {number} deltaTime - Time elapsed in milliseconds
     */
    update(deltaTime) {
        if (this.isPaused) {
            return;
        }
        
        // Apply speed multiplier
        const adjustedDeltaTime = deltaTime * this.gameSpeed;
        
        // Update timers
        this.timerManager.update(adjustedDeltaTime);
        
        // Update resources based on production
        this.updateResources(adjustedDeltaTime);
        
        // Check for completed constructions
        this.checkConstructionQueue();
    }
    
    /**
     * Update resources based on production rates
     * @param {number} deltaTime - Time elapsed in milliseconds
     */
    updateResources(deltaTime) {
        // Calculate hourly production from GameEngine
        this.gameEngine.updateTotals();
        
        const hourlyProduction = {
            wood: this.gameEngine.wood_tot,
            stone: this.gameEngine.stone_tot,
            iron: this.gameEngine.iron_tot,
            food: this.gameEngine.food_tot,
            gold: this.gameEngine.gold_tot
        };
        
        // Convert to per-millisecond production
        const msPerHour = 3600000; // 1000 * 60 * 60
        const production = {
            wood: (hourlyProduction.wood / msPerHour) * deltaTime,
            stone: (hourlyProduction.stone / msPerHour) * deltaTime,
            iron: (hourlyProduction.iron / msPerHour) * deltaTime,
            food: (hourlyProduction.food / msPerHour) * deltaTime,
            gold: (hourlyProduction.gold / msPerHour) * deltaTime
        };
        
        // Update storage capacities
        this.updateStorage();
        
        // Add production to resources, respecting storage limits
        const oldResources = { ...this.resources };
        
        this.resources.wood = Math.min(
            this.resources.wood + production.wood,
            this.storage.wood
        );
        this.resources.stone = Math.min(
            this.resources.stone + production.stone,
            this.storage.stone
        );
        this.resources.iron = Math.min(
            this.resources.iron + production.iron,
            this.storage.iron
        );
        this.resources.food = Math.min(
            this.resources.food + production.food,
            this.storage.food
        );
        // Gold has no storage limit
        this.resources.gold += production.gold;
        
        // Emit resource update event if values changed
        if (this.hasResourceChanged(oldResources, this.resources)) {
            this.emit('resourceUpdated', this.resources);
        }
    }
    
    /**
     * Check if resources have changed
     * @param {Object} oldResources - Previous resource values
     * @param {Object} newResources - New resource values
     * @returns {boolean} True if any resource changed
     */
    hasResourceChanged(oldResources, newResources) {
        return oldResources.wood !== newResources.wood ||
               oldResources.stone !== newResources.stone ||
               oldResources.iron !== newResources.iron ||
               oldResources.food !== newResources.food ||
               oldResources.gold !== newResources.gold;
    }
    
    /**
     * Update storage capacities from GameEngine
     */
    updateStorage() {
        this.gameEngine.updateTotals();
        
        this.storage.wood = this.gameEngine.wood_storage;
        this.storage.stone = this.gameEngine.stone_storage;
        this.storage.iron = this.gameEngine.iron_storage;
        this.storage.food = this.gameEngine.food_storage;
        
        // Update hidden storage (caches)
        this.hiddenResources.wood = this.gameEngine.wood_hidden;
        this.hiddenResources.stone = this.gameEngine.stone_hidden;
        this.hiddenResources.iron = this.gameEngine.iron_hidden;
        this.hiddenResources.food = this.gameEngine.food_hidden;
    }
    
    /**
     * Check construction queue for completed buildings
     */
    checkConstructionQueue() {
        // ConstructionManager handles this via timers
        // This method is kept for compatibility
        this.constructionManager.processQueue();
    }
    
    /**
     * Get current resources
     * @returns {Object} Resource object
     */
    getResources() {
        return { ...this.resources };
    }
    
    /**
     * Get storage capacities
     * @returns {Object} Storage object
     */
    getStorage() {
        return { ...this.storage };
    }
    
    /**
     * Check if player has enough resources
     * @param {Object} costs - Object with wood, stone, iron, food, gold
     * @returns {boolean} True if player has enough resources
     */
    hasResources(costs) {
        return (!costs.wood || this.resources.wood >= costs.wood) &&
               (!costs.stone || this.resources.stone >= costs.stone) &&
               (!costs.iron || this.resources.iron >= costs.iron) &&
               (!costs.food || this.resources.food >= costs.food) &&
               (!costs.gold || this.resources.gold >= costs.gold);
    }
    
    /**
     * Spend resources (with validation)
     * @param {Object} costs - Object with wood, stone, iron, food, gold
     * @returns {boolean} True if resources were spent successfully
     */
    spendResources(costs) {
        if (!this.hasResources(costs)) {
            return false;
        }
        
        if (costs.wood) this.resources.wood -= costs.wood;
        if (costs.stone) this.resources.stone -= costs.stone;
        if (costs.iron) this.resources.iron -= costs.iron;
        if (costs.food) this.resources.food -= costs.food;
        if (costs.gold) this.resources.gold -= costs.gold;
        
        this.emit('resourceUpdated', this.resources);
        return true;
    }
    
    /**
     * Add resources (for testing, events, etc.)
     * @param {Object} amounts - Object with wood, stone, iron, food, gold
     */
    addResources(amounts) {
        if (amounts.wood) {
            this.resources.wood = Math.min(
                this.resources.wood + amounts.wood,
                this.storage.wood
            );
        }
        if (amounts.stone) {
            this.resources.stone = Math.min(
                this.resources.stone + amounts.stone,
                this.storage.stone
            );
        }
        if (amounts.iron) {
            this.resources.iron = Math.min(
                this.resources.iron + amounts.iron,
                this.storage.iron
            );
        }
        if (amounts.food) {
            this.resources.food = Math.min(
                this.resources.food + amounts.food,
                this.storage.food
            );
        }
        if (amounts.gold) {
            this.resources.gold += amounts.gold;
        }
        
        this.emit('resourceUpdated', this.resources);
    }
    
    /**
     * Pause the game
     */
    pause() {
        this.isPaused = true;
        this.timerManager.pauseAll();
    }
    
    /**
     * Resume the game
     */
    resume() {
        this.isPaused = false;
        this.timerManager.resumeAll();
        this.lastUpdateTime = Date.now();
    }
    
    /**
     * Set game speed
     * @param {number} speed - Speed multiplier (1.0, 2.0, 4.0)
     */
    setSpeed(speed) {
        this.gameSpeed = Math.max(0.25, Math.min(4.0, speed));
    }
    
    /**
     * Save game state
     * @returns {boolean} Success status
     */
    save() {
        return this.saveManager.save(this);
    }
    
    /**
     * Load game state
     * @returns {boolean} Success status
     */
    load() {
        const data = this.saveManager.load();
        if (!data) {
            return false;
        }
        
        this.deserialize(data);
        return true;
    }
    
    /**
     * Serialize game state for saving
     * @returns {Object} Serialized state
     */
    serialize() {
        return {
            resources: { ...this.resources },
            lastUpdateTime: this.lastUpdateTime,
            isPaused: this.isPaused,
            gameSpeed: this.gameSpeed,
            constructionQueue: this.constructionManager.getQueue().map(item => ({
                ...item,
                // Don't serialize functions or timer references
                timerId: undefined,
                onComplete: undefined
            })),
            // Serialize GameEngine map state
            gameEngineState: {
                map: this.gameEngine.map,
                buildingLevel: this.gameEngine.buildingLevel,
                basilicaLevel: this.gameEngine.basilicaLevel
            }
        };
    }
    
    /**
     * Deserialize game state from saved data
     * @param {Object} data - Serialized state
     */
    deserialize(data) {
        if (data.resources) {
            this.resources = { ...data.resources };
        }
        
        if (data.lastUpdateTime) {
            this.lastUpdateTime = data.lastUpdateTime;
        }
        
        if (data.isPaused !== undefined) {
            this.isPaused = data.isPaused;
        }
        
        if (data.gameSpeed !== undefined) {
            this.gameSpeed = data.gameSpeed;
        }
        
        if (data.constructionQueue) {
            // Restore construction queue items
            // Note: Active timers won't be restored, but that's okay for now
            // In a full implementation, we'd need to recreate timers
            for (const item of data.constructionQueue) {
                if (item.status === 'building') {
                    // Recreate timer for building items
                    const remainingTime = item.completesAt ? 
                        Math.max(0, (item.completesAt - Date.now()) / 1000) : 
                        item.buildTime;
                    
                    if (remainingTime > 0) {
                        item.timerId = this.timerManager.createTimer(
                            'construction',
                            remainingTime,
                            (timerId, data) => {
                                this.constructionManager.completeConstruction(data.queueItemId);
                            },
                            { queueItemId: item.id }
                        );
                    }
                }
                // Add to construction manager's queue
                this.constructionManager.queue.push(item);
            }
        }
        
        if (data.gameEngineState) {
            // Restore GameEngine state
            if (data.gameEngineState.map) {
                this.gameEngine.map = data.gameEngineState.map;
            }
            if (data.gameEngineState.buildingLevel !== undefined) {
                this.gameEngine.buildingLevel = data.gameEngineState.buildingLevel;
            }
            if (data.gameEngineState.basilicaLevel !== undefined) {
                this.gameEngine.basilicaLevel = data.gameEngineState.basilicaLevel;
            }
            
            // Recalculate totals
            this.gameEngine.updateTotals();
            this.updateStorage();
        }
    }
    
    /**
     * Add event listener
     * @param {string} event - Event name
     * @param {Function} callback - Callback function
     */
    on(event, callback) {
        if (this.eventListeners[event]) {
            this.eventListeners[event].push(callback);
        }
    }
    
    /**
     * Remove event listener
     * @param {string} event - Event name
     * @param {Function} callback - Callback function
     */
    off(event, callback) {
        if (this.eventListeners[event]) {
            const index = this.eventListeners[event].indexOf(callback);
            if (index > -1) {
                this.eventListeners[event].splice(index, 1);
            }
        }
    }
    
    /**
     * Emit event
     * @param {string} event - Event name
     * @param {*} data - Event data
     */
    emit(event, data) {
        if (this.eventListeners[event]) {
            this.eventListeners[event].forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    console.error('Error in event listener:', error);
                }
            });
        }
    }
}

